/**
 * MoneyMojo App Shell - Main Application Component
 * 
 * This is the root component that establishes the site-wide layout
 * and navigation chrome as specified in the requirements.
 * 
 * Features:
 * - App shell with Header, Nav, Pages, Footer
 * - Region-aware header utility area (AU currency/date display)
 * - Theme switching (dark/light)
 * - Accessibility compliance (WCAG 2.2 AA)
 * - Skip-to-content link
 * - Page slots ready for feature pages
 * 
 * @see .specify/memory/constitution.md
 * @see /Reference/Reference design-review/design-principles.md
 */

import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// App shell components
import { App as AppShell } from './components/app-shell/App';
import { Header } from './components/app-shell/Header';
import { Navigation } from './components/app-shell/Navigation';
import { Pages } from './components/app-shell/Pages';
import { Footer } from './components/app-shell/Footer';

// Theme and Region
import { ThemeProvider } from './contexts/ThemeContext';
import { RegionProvider } from './contexts/RegionContext';
import { SessionTimeoutProvider } from './contexts/SessionTimeoutProvider';
import { MobileMenuProvider } from './contexts/MobileMenuContext';
import { MojoAgentProvider } from './contexts/MojoAgentContext';
import { MojoAgentLayout } from './components/mojo-agent/MojoAgentLayout';
import { createAURegionPack } from './lib/regions/au';

// Page Components (thin wrappers for routes)
import { DashboardPage } from './pages/DashboardPage';
import { TransactionsPage } from './pages/TransactionsPage';
import { AccountsPage } from './pages/AccountsPage';
import { BudgetsPage } from './pages/BudgetsPage';
import { InsightsPage } from './pages/InsightsPage';
import { InsightsChatPage } from './pages/InsightsChatPage';
import { ReportingPage } from './pages/ReportingPage';
import { RecentReportsPage } from './pages/reporting/RecentReportsPage';
import { SettingsPage } from './pages/SettingsPage';
import { AuthPage } from './pages/AuthPage';
import { FinancialPlannerPage } from './pages/FinancialPlannerPage';
import { StyleGuidePage } from './pages/admin/StyleGuidePage';
import { AccessibilityGuidePage } from './pages/admin/AccessibilityGuidePage';
import { HelpCenterPage } from './pages/HelpCenterPage';
import { HelpArticlePage } from './pages/HelpArticlePage';
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage';
import { TermsOfServicePage } from './pages/TermsOfServicePage';
import { AboutUsPage } from './pages/AboutUsPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { AuthLayout } from './components/AuthLayout';
import { LegalPageLayout } from './components/LegalPageLayout';
import { ProtectedRoute } from './components/ProtectedRoute';
import { ScrollToTop } from './components/ScrollToTop';

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

/**
 * Main App Component
 * 
 * This component establishes the app shell structure with:
 * - Header with region-aware utility area
 * - Navigation with all main sections
 * - Page slots for feature pages
 * - Footer with accessibility links
 * - Theme and region context providers
 */
export function App(): React.ReactElement {
  return (
    <QueryClientProvider client={queryClient}>
      <RegionProvider regionPack={createAURegionPack()}>
        <Router
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <ScrollToTop />
          <Routes>
            {/* Authentication routes with standalone layout */}
            <Route path="/auth" element={
              <AuthLayout>
                <AuthPage />
              </AuthLayout>
            } />
            <Route path="/login" element={
              <AuthLayout>
                <AuthPage mode="login" />
              </AuthLayout>
            } />
            <Route path="/signup" element={
              <AuthLayout>
                <AuthPage mode="signup" />
              </AuthLayout>
            } />
            <Route path="/forgot-password" element={
              <AuthLayout>
                <ForgotPasswordPage />
              </AuthLayout>
            } />
            <Route path="/reset-password" element={
              <AuthLayout>
                <ResetPasswordPage />
              </AuthLayout>
            } />
            
            {/* Public legal pages - shows LegalPageLayout (breadcrumb only for unauthenticated users) */}
            <Route path="/terms" element={
              <LegalPageLayout title="Terms of Service">
                <TermsOfServicePage />
              </LegalPageLayout>
            } />
            <Route path="/privacy-policy" element={
              <LegalPageLayout title="Privacy Policy">
                <PrivacyPolicyPage />
              </LegalPageLayout>
            } />
            <Route path="/about" element={
              <LegalPageLayout title="About Us">
                <AboutUsPage />
              </LegalPageLayout>
            } />
            
            {/* Main app routes with full layout - PROTECTED */}
            <Route path="/*" element={
              <ProtectedRoute>
                <ThemeProvider>
                  <SessionTimeoutProvider>
                    <MobileMenuProvider>
                    <MojoAgentProvider>
                    <AppShell>
                    <MojoAgentLayout>
                    {/* Skip to content link for accessibility */}
                    <a 
                      href="#main-content" 
                      className="skip-to-content focus-ring"
                      aria-label="Skip to main content"
                    >
                      Skip to main content
                    </a>
                    
                    {/* Header with navigation and region utilities */}
                    <Header />
                    
                    {/* Main navigation */}
                    <Navigation />
                    
                    {/* Page content area */}
                    <Pages>
                      <Routes>
                        <Route path="/" element={<Navigate to="/dashboard" replace />} />
                        <Route path="/dashboard" element={<DashboardPage />} />
                        <Route path="/transactions" element={<TransactionsPage />} />
                        <Route path="/accounts" element={<AccountsPage />} />
                        <Route path="/budgets" element={<BudgetsPage />} />
                        <Route path="/goals" element={<Navigate to="/budgets?tab=savings" replace />} />
                        <Route path="/insights" element={<InsightsPage />} />
                        <Route path="/insights/chat" element={<InsightsChatPage />} />
                        <Route path="/reporting" element={<ReportingPage />} />
                        <Route path="/reporting/history" element={<RecentReportsPage />} />
                        <Route path="/help" element={<HelpCenterPage />} />
                        <Route path="/help/:slug" element={<HelpArticlePage />} />
                        {/* Legal pages for authenticated users - will use full app layout */}
                        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                        <Route path="/terms" element={<TermsOfServicePage />} />
                        <Route path="/about" element={<AboutUsPage />} />
                        <Route path="/settings/*" element={<SettingsPage />} />
                        <Route path="/financial-planner" element={<FinancialPlannerPage />} />
                        <Route path="/admin/style-guide" element={<StyleGuidePage />} />
                        <Route path="/admin/accessibility" element={<AccessibilityGuidePage />} />
                        
                        {/* Catch-all route for 404 */}
                        <Route path="*" element={<Navigate to="/dashboard" replace />} />
                      </Routes>
                    </Pages>
                    
                    {/* Footer */}
                    <Footer />
                    </MojoAgentLayout>
                    </AppShell>
                    </MojoAgentProvider>
                    </MobileMenuProvider>
                  </SessionTimeoutProvider>
                </ThemeProvider>
              </ProtectedRoute>
            } />
          </Routes>
        </Router>
      </RegionProvider>
    </QueryClientProvider>
  );
}

export default App;
